import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import Sheet from '../../shared/ui/Sheet';
import { validateOrderQRCode } from '../../shared/services/orderService';
import { isNativeScanner, scanOnce } from '../../../../utils/nativeScan';

const READER_ID = 'driver-qr-reader';

// Order-ID extraction — mirrors iOS OCRScannerViewModel.extractOrderId:
// Firestore-style IDs (20+ alphanumeric), "order/ord" prefixed strings,
// UUIDs, then fallback to the longest alphanumeric word (>= 8 chars).
function extractOrderId(text) {
  const patterns = [
    /[A-Za-z0-9]{20,}/,
    /(?:order|ord)[-_]?[A-Za-z0-9]+/i,
    /[A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12}/,
  ];
  for (const re of patterns) {
    const m = text.match(re);
    if (m) {
      const candidate = m[0].trim();
      if (candidate.length >= 8 && /^[A-Za-z0-9]+$/.test(candidate)) return candidate;
    }
  }
  const words = text.split(/\s+/).filter((w) => /^[A-Za-z0-9]+$/.test(w));
  const longest = words.reduce((a, b) => (b.length > a.length ? b : a), '');
  return longest.length >= 8 ? longest : null;
}

// Order-verification sheet (mirrors iOS ScanOrderView): camera QR scan via
// html5-qrcode + OCR text scan via tesseract.js + manual order-ID entry.
// Validation goes through the shared validateOrderQRCode callable. Like iOS,
// this is an aid — progression is not hard-blocked on a successful scan.
export default function ScanSheet({ open, onClose, order, driverId, onVerified }) {
  const [manualMode, setManualMode] = useState(false);
  const [manualId, setManualId] = useState('');
  const [status, setStatus] = useState(null); // {kind:'busy'|'error'|'success', msg}
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const scannerRef = useRef(null);
  const busyRef = useRef(false);
  const ocrWorkerRef = useRef(null);
  const ocrInFlightRef = useRef(false);
  const ocrTimerRef = useRef(null);
  const orderRef = useRef(order);
  const driverIdRef = useRef(driverId);
  const onVerifiedRef = useRef(onVerified);
  orderRef.current = order;
  driverIdRef.current = driverId;
  onVerifiedRef.current = onVerified;

  // Camera path: qrText is the raw QR payload; the callable needs the
  // orderId pulled out of it (it validates qrCodeData against the order).
  async function validate(qrText) {
    setStatus({ kind: 'busy', msg: 'Validating order…' });
    try {
      let parsed;
      try { parsed = JSON.parse(qrText); } catch { parsed = null; }
      const orderId = parsed?.orderId || orderRef.current?.orderId;
      const res = await validateOrderQRCode({
        qrCodeData: qrText,
        orderId,
        driverId: driverIdRef.current,
        manualEntry: false,
      });
      if (res?.success === true) {
        setStatus({ kind: 'success', msg: '✅ Order verified!' });
        onVerifiedRef.current?.();
        try {
          await scannerRef.current?.stop();
          scannerRef.current?.clear();
        } catch { /* already stopped */ }
        return true;
      }
      setStatus({
        kind: 'error',
        msg: res?.message || 'Invalid order code. Please try again.',
      });
      return false;
    } catch (e) {
      setStatus({ kind: 'error', msg: e?.message || 'Validation failed. Try again.' });
      return false;
    }
  }

  // OCR path (mirrors iOS validateOCROrderId): the detected candidate must
  // equal this order's id, then it's validated with manualEntry: true —
  // iOS treats OCR as manual entry.
  async function validateOcr(candidate) {
    if (busyRef.current) return;
    const expected = orderRef.current?.orderId;
    // Accept the full ID or the 8-char order number printed on receipts —
    // case-insensitive so drivers don't have to match capitalization.
    const norm = expected?.toLowerCase();
    const cand = candidate?.toLowerCase();
    if (!norm || (cand !== norm && !(cand.length >= 8 && norm.startsWith(cand)))) return;
    busyRef.current = true;
    setStatus({ kind: 'busy', msg: 'Validating order…' });
    try {
      const res = await validateOrderQRCode({
        orderId: expected,
        driverId: driverIdRef.current,
        manualEntry: true,
      });
      if (res?.success === true) {
        setStatus({ kind: 'success', msg: '✅ Order verified!' });
        onVerifiedRef.current?.();
        try {
          await scannerRef.current?.stop();
          scannerRef.current?.clear();
        } catch { /* already stopped */ }
      } else {
        setStatus({ kind: 'error', msg: res?.message || 'Invalid order ID. Please try again.' });
      }
    } catch (e) {
      setStatus({ kind: 'error', msg: e?.message || 'Validation failed. Try again.' });
    } finally {
      busyRef.current = false;
    }
  }

  // Grabs a frame from the html5-qrcode video element and runs tesseract on
  // it — the web equivalent of iOS processImage on the camera buffer. Frames
  // are downscaled to keep recognition fast; runs at most one job at a time.
  async function runOcrFrame() {
    if (ocrInFlightRef.current || busyRef.current) return;
    const video = document.querySelector(`#${READER_ID} video`);
    if (!video || video.readyState < 2 || !video.videoWidth) return;
    const worker = ocrWorkerRef.current;
    if (!worker) return;

    ocrInFlightRef.current = true;
    try {
      const scale = Math.min(1, 960 / video.videoWidth);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
      const { data } = await worker.recognize(canvas);
      const candidate = extractOrderId(data?.text || '');
      if (candidate) await validateOcr(candidate);
    } catch {
      // OCR is best-effort — a bad frame shouldn't surface an error.
    } finally {
      ocrInFlightRef.current = false;
    }
  }

  // Lazily loads tesseract.js (heavy) only once the camera is actually up.
  async function startOcrLoop() {
    try {
      if (!ocrWorkerRef.current) {
        const { createWorker } = await import('tesseract.js');
        ocrWorkerRef.current = await createWorker('eng');
      }
      ocrTimerRef.current = setInterval(runOcrFrame, 3000);
    } catch {
      // OCR unavailable (e.g. worker failed to load) — QR + manual still work.
    }
  }

  async function stopOcrLoop() {
    if (ocrTimerRef.current) {
      clearInterval(ocrTimerRef.current);
      ocrTimerRef.current = null;
    }
    if (ocrWorkerRef.current) {
      try { await ocrWorkerRef.current.terminate(); } catch { /* noop */ }
      ocrWorkerRef.current = null;
    }
  }

  // Native path — full-screen ML Kit scanner (torch/zoom/close built in).
  // scanOnce returns null on cancel, so the driver lands back on this
  // sheet and can scan again or enter the ID manually.
  async function runNativeScan() {
    setStatus(null);
    const text = await scanOnce();
    if (text != null) await validate(text);
  }

  // Camera lifecycle — start when the sheet opens, stop on close/unmount.
  useEffect(() => {
    if (!open) return undefined;
    setStatus(null);
    setManualMode(false);
    setManualId('');
    setTorchOn(false);
    setTorchSupported(false);
    let cancelled = false;

    // On Android the ML Kit scanner owns the camera — no html5 reader.
    if (isNativeScanner()) {
      runNativeScan();
      return () => {};
    }

    const timer = setTimeout(() => {
      if (cancelled || !document.getElementById(READER_ID)) return;
      const scanner = new Html5Qrcode(READER_ID);
      scannerRef.current = scanner;
      scanner
        .start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 230, height: 230 } },
          (decoded) => {
            if (busyRef.current) return;
            busyRef.current = true;
            validate(decoded).finally(() => {
              busyRef.current = false;
            });
          },
          () => {}
        )
        .then(() => {
          if (cancelled) return;
          startOcrLoop();
          // Torch support — Chrome/Android exposes it via the video track.
          try {
            const video = document.querySelector(`#${READER_ID} video`);
            const track = video?.srcObject?.getVideoTracks?.()[0];
            const caps = track?.getCapabilities?.();
            if (caps && 'torch' in caps) setTorchSupported(true);
          } catch { /* torch unsupported */ }
        })
        .catch((e) => {
          setStatus({
            kind: 'error',
            msg: `Camera unavailable (${e?.message || e}). You can enter the order ID manually.`,
          });
        });
    }, 100);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      stopOcrLoop();
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        try {
          s.stop()
            .then(() => s.clear())
            .catch(() => {
              try {
                s.clear();
              } catch { /* noop */ }
            });
        } catch {
          // stop() throws synchronously when the scanner never started
          try { s.clear(); } catch { /* noop */ }
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Camera torch (flash) — toggled through the running video track. Only
  // shown when the browser/device reports torch capability.
  async function toggleTorch() {
    try {
      const video = document.querySelector(`#${READER_ID} video`);
      const track = video?.srcObject?.getVideoTracks?.()[0];
      if (!track) return;
      const next = !torchOn;
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch { /* torch failed — leave state as-is */ }
  }

  async function submitManual() {
    const trimmed = manualId.trim();
    if (!trimmed || busyRef.current) return;
    // Mirror iOS: manual entry must match this order — the full ID or the
    // 8-char order number printed on the receipt (case-insensitive).
    const expected = orderRef.current?.orderId || '';
    const norm = expected.toLowerCase();
    const typed = trimmed.toLowerCase();
    if (orderRef.current && typed !== norm && !(typed.length >= 8 && norm.startsWith(typed))) {
      setStatus({ kind: 'error', msg: 'That order ID does not match this delivery.' });
      return;
    }
    busyRef.current = true;
    setStatus({ kind: 'busy', msg: 'Validating order…' });
    try {
      const res = await validateOrderQRCode({
        orderId: expected || trimmed,
        driverId: driverIdRef.current,
        manualEntry: true,
      });
      if (res?.success === true) {
        setStatus({ kind: 'success', msg: '✅ Order verified!' });
        onVerifiedRef.current?.();
      } else {
        setStatus({ kind: 'error', msg: res?.message || 'Invalid order ID. Please try again.' });
      }
    } catch (e) {
      setStatus({ kind: 'error', msg: e?.message || 'Validation failed. Try again.' });
    } finally {
      busyRef.current = false;
    }
  }

  const num = order ? String(order.orderId || order.id).slice(0, 8) : '';

  return (
    <Sheet open={open} onClose={onClose} title="Scan Order Receipt" maxHeight="92vh">
      <div className="space-y-4 p-4 pt-1">
        <p className="text-center text-xs text-gray-500">
          Scan the QR code or the printed order ID on the receipt — or enter it manually.
        </p>

        <div className="rounded-2xl bg-white p-3 text-center shadow-sm border border-gray-100">
          <p className="text-lg font-extrabold text-gray-900">Order #{num}</p>
          <p className="text-xs text-gray-500">Restaurant: {order?.restaurantName}</p>
        </div>

        {!manualMode ? (
          <>
            {isNativeScanner() ? (
              <button
                onClick={runNativeScan}
                className="w-full rounded-2xl bg-black py-10 text-sm font-semibold text-white flex flex-col items-center gap-2"
              >
                <span className="text-3xl">📷</span>
                Tap to Scan QR Code
              </button>
            ) : (
              <div className="relative overflow-hidden rounded-2xl bg-black">
                <div id={READER_ID} className="h-64 w-full" />
                {torchSupported && (
                  <button
                    onClick={toggleTorch}
                    aria-label="Toggle flash"
                    className={`absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full text-base ${
                      torchOn ? 'bg-yellow-400 text-black' : 'bg-black/55 text-white'
                    }`}
                  >
                    {torchOn ? '🔦' : '⚡'}
                  </button>
                )}
                <p className="pointer-events-none absolute inset-x-0 bottom-2 text-center">
                  <span className="rounded-lg bg-black/60 px-2 py-1 text-[11px] text-white">
                    Position the QR code within the frame
                  </span>
                </p>
              </div>
            )}
            <button
              onClick={() => setManualMode(true)}
              className="w-full rounded-xl bg-blue-50 py-3 text-sm font-semibold text-blue-600"
            >
              Enter Order ID Manually
            </button>
          </>
        ) : (
          <div className="space-y-3">
            <input
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              placeholder="Order ID"
              autoCapitalize="none"
              autoCorrect="off"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FF6B35]/40"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setManualMode(false)}
                className="flex-1 rounded-xl bg-gray-100 py-2.5 text-sm font-semibold text-gray-600"
              >
                Back
              </button>
              <button
                onClick={submitManual}
                disabled={!manualId.trim() || status?.kind === 'busy'}
                className="flex-1 rounded-xl bg-[#FF6B35] py-2.5 text-sm font-bold text-white disabled:opacity-50"
              >
                Confirm
              </button>
            </div>
          </div>
        )}

        {status && (
          <p
            className={`rounded-xl p-3 text-center text-xs font-semibold ${
              status.kind === 'error'
                ? 'bg-red-50 text-red-600'
                : status.kind === 'success'
                  ? 'bg-green-50 text-green-600'
                  : 'bg-gray-50 text-gray-500'
            }`}
          >
            {status.msg}
          </p>
        )}

        <button
          onClick={onClose}
          className="w-full rounded-xl bg-gray-100 py-3 text-sm font-semibold text-gray-500"
        >
          Cancel
        </button>
      </div>
    </Sheet>
  );
}
